<template>
    <div class="page-container">
        <!-- Desktop View -->
        <div class="desktop-view" v-if="!isMobile">
            <div class="page-header">
                <div class="header-content" v-if="isNewsletterModuleEnabled">
                    <h1 class="page-title">All Newsletters</h1>
                    <p class="page-subtitle">Browse our complete collection of past editions, expert insights, and community updates.</p>
                </div>
            </div>

            <!-- Disabled Info (Desktop - large centered card) -->
            <div v-if="!isNewsletterModuleEnabled" class="feature-disabled-container">
                <div class="disabled-content">
                    <i class="fas fa-toggle-off fa-3x mb-3 text-primary"></i>
                    <h4>Newsletter Module Disabled</h4>
                    <p>The Newsletter Module has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
                </div>
            </div>

            <div v-if="isNewsletterModuleEnabled" class="content-wrapper">
                <!-- Main Content -->
                <div class="main-content">
                    <div v-if="loading" class="text-center p-5">
                        <div class="spinner-border text-primary" role="status"></div>
                    </div>

                    <div v-else class="newsletter-list">
                        <template v-if="newsletters.length > 0">
                            <div 
                                v-for="(item, index) in newsletters" 
                                :key="item.id || index"
                                class="newsletter-card"
                                :class="{ 'is-unread': !item.is_read }"
                                @click="goToDetail(item.id)"
                            >
                                <div class="newsletter-info">
                                    <div class="newsletter-image">
                                        <img :src="item.cover_image || defaultLogo" alt="Newsletter">
                                    </div>
                                    <div class="newsletter-detail">
                                        <h1 class="newsletter-title" :title="item.title">{{ truncate(item.title, 50) }}</h1>
                                        <p class="newsletter-publisher">
                                            <template v-if="item.merchant_id">
                                                {{ item.merchant_name }} • {{ item.branch_name }}
                                            </template>
                                            <template v-else-if="item.branch_id">
                                                {{ item.branch_name }}
                                            </template>
                                            <template v-else>
                                                {{ item.corporate_name }}
                                            </template>
                                        </p>
                                        <div class="newsletter-date">{{ formatDate(item.published_at) }}</div>
                                    </div>
                                </div>

                                <img src="/img/member-icon/arrow-right.svg" alt="" class="newsletter-chevron">

                                <div v-if="!item.is_read" class="newsletter-badge">
                                    <span>NEW</span>
                                </div>
                            </div>
                        </template>
                        <no-items v-else message="No newsletters found" transparent />
                    </div>

                    <!-- Pagination -->
                    <!-- Changed condition to match TransactionHistory: show if items exist, don't check total > 1 -->
                    <member-pagination 
                        v-if="!loading && newsletters.length > 0" 
                        :page="meta.page" 
                        :total="meta.total" 
                        @callback="navPage" 
                    />
                </div>

                <!-- Sidebar Filter & Search -->
                <div class="sidebar-menu">
                    <!-- Filter Card -->
                    <div class="filter-card">
                        <h3 class="card-title">Search</h3>
                        <div class="search-box mb-4">
                            <i class="fas fa-search search-icon" @click="handleSearch"></i>
                            <input 
                                v-model="searchQuery" 
                                @keyup.enter="handleSearch"
                                type="text" 
                                placeholder="Search..."
                            />
                            <i v-if="searchQuery" class="fas fa-times clear-icon" @click="clearSearch"></i>
                        </div>

                        <h3 class="card-title">Filter</h3>
                        
                        <div class="filter-group">
                            <label class="filter-label">Unit / Branch</label>
                            <div class="custom-select">
                                <select v-model="selectedBranch" @change="updateMerchants">
                                    <option value="">All Branch</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Merchant / Outlet</label>
                            <div class="custom-select">
                                <select v-model="selectedMerchant">
                                    <option value="">All Merchant</option>
                                    <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.id">
                                        {{ merchant.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Date Range</label>
                            <div class="date-range-inputs">
                                <DatePicker
                                    v-model="startDate"
                                    :max="endDate"
                                    placeholder="Start Date"
                                    class="date-picker"
                                />
                                <span class="date-separator">to</span>
                                <DatePicker
                                    v-model="endDate"
                                    :min="startDate"
                                    placeholder="End Date"
                                    class="date-picker"
                                />
                            </div>
                        </div>

                        <div class="button-group">
                            <button @click="applyFilter" class="btn-apply">Apply Filter</button>
                            <button @click="clearFilter" class="btn-clear">Clear Filter</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile View -->
        <template v-if="isMobile">
            <div class="top-background">
                <svg width="330" height="55" viewBox="0 0 330 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="111" cy="-84" rx="219" ry="139" fill="currentColor"/>
                </svg>
            </div>
            <div class="mobile-view">
                <div class="mobile-header">
                    <h2 class="mobile-title" v-if="isNewsletterModuleEnabled">All Newsletters</h2>
                    <div class="mobile-actions" v-if="isNewsletterModuleEnabled">
                        <button @click="isFilterModalOpen = true" class="filter-button">
                            <img src="/img/member-icon/filter-icon.svg" alt="filter-icon" width="14" height="14">
                        </button>
                    </div>
                </div>

                <!-- Disabled Info (Mobile) -->
                <div v-if="!isNewsletterModuleEnabled" class="feature-disabled-inline mobile-disabled">
                    <i class="fas fa-toggle-off"></i>
                    <div>
                        <h4>Newsletter Module Disabled</h4>
                        <p>This feature has been disabled by your administrator.</p>
                    </div>
                </div>

                <template v-if="isNewsletterModuleEnabled">
                <div class="newsletter-list">
                     <!-- Mobile Search -->
                    <div class="search-box mb-3">
                        <i class="fas fa-search search-icon"></i>
                        <input 
                            v-model="searchQuery" 
                            @keyup.enter="handleSearch"
                            type="text" 
                            placeholder="Search..."
                        />
                        <i v-if="searchQuery" class="fas fa-times clear-icon" @click="clearSearch"></i>
                    </div>

                    <div v-if="loading" class="text-center p-5">
                        <div class="spinner-border text-primary" role="status"></div>
                    </div>

                    <template v-else-if="newsletters.length > 0">
                        <div 
                            v-for="(item, index) in newsletters" 
                            :key="item.id || index"
                            class="newsletter-card"
                            :class="{ 'is-unread': !item.is_read }"
                            @click="goToDetail(item.id)"
                        >
                            <div class="newsletter-info">
                                <div class="newsletter-image">
                                    <img :src="item.cover_image || defaultLogo" alt="Newsletter">
                                </div>
                                <div class="newsletter-detail">
                                    <h1 class="newsletter-title" :title="item.title">{{ truncate(item.title, 50) }}</h1>
                                    <p class="newsletter-publisher">
                                        <template v-if="item.merchant_id">
                                            {{ item.merchant_name }} • {{ item.branch_name }}
                                        </template>
                                        <template v-else-if="item.branch_id">
                                            {{ item.branch_name }}
                                        </template>
                                        <template v-else>
                                            {{ item.corporate_name }}
                                        </template>
                                    </p>
                                    <div class="newsletter-date">{{ formatDate(item.published_at) }}</div>
                                </div>
                            </div>

                            <img src="/img/member-icon/arrow-right.svg" alt="" class="newsletter-chevron">

                            <div v-if="!item.is_read" class="newsletter-badge">
                                <span>NEW</span>
                            </div>
                        </div>
                    </template>
                    <no-items v-else message="No newsletters found" />
                </div>

                 <!-- Pagination -->
                <member-pagination 
                    v-if="!loading && newsletters.length > 0" 
                    :page="meta.page" 
                    :total="meta.total" 
                    @callback="navPage" 
                />

                <!-- Filter Modal -->
                <div v-if="isFilterModalOpen" class="modal-overlay" @click="isFilterModalOpen = false">
                    <div class="modal-content" @click.stop>
                        <div class="modal-header">
                            <h3>Filter</h3>
                            <button @click="isFilterModalOpen = false" class="close-button">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                            </button>
                        </div>

                        <div class="modal-body">
                             <div class="filter-group">
                                <label class="filter-label">Unit / Branch</label>
                                <div class="custom-select">
                                    <select v-model="selectedBranch" @change="updateMerchants">
                                        <option value="">All Branch</option>
                                        <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                                            {{ branch.name }}
                                        </option>
                                    </select>
                                    <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                            </div>

                            <div class="filter-group">
                                <label class="filter-label">Merchant / Outlet</label>
                                <div class="custom-select">
                                    <select v-model="selectedMerchant">
                                        <option value="">All Merchant</option>
                                        <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.id">
                                            {{ merchant.name }}
                                        </option>
                                    </select>
                                    <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                            </div>

                            <div class="filter-group">
                                <label class="filter-label">Date Range</label>
                                <div class="date-range-inputs mobile">
                                    <DatePicker
                                        v-model="startDate"
                                        :max="endDate"
                                        placeholder="Start Date"
                                        class="date-picker"
                                    />
                                    <span class="date-separator">to</span>
                                    <DatePicker
                                        v-model="endDate"
                                        :min="startDate"
                                        placeholder="End Date"
                                        class="date-picker"
                                    />
                                </div>
                            </div>

                            <div class="button-group">
                                <button @click="applyFilter" class="btn-apply">Apply Filter</button>
                                <button @click="clearFilter" class="btn-clear">Clear Filter</button>
                            </div>
                        </div>
                    </div>
                </div>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import MemberPagination from '@/components/member/MemberPagination.vue'
import DatePicker from '@/components/DatePicker.vue'
import NoItems from '@/components/member/NoItems.vue'

export default {
    name: 'MemberNewsletterList',
    components: {
        MemberPagination,
        DatePicker,
        NoItems
    },
    setup() {
        const store = useStore()
        const router = useRouter()
        
        const loading = computed(() => store.getters['ClientNewsletter/isLoading'])
        const newsletters = computed(() => store.state.ClientNewsletter.newsletters)
        const meta = computed(() => store.state.ClientNewsletter.meta)
        
        const defaultLogo = computed(() => {
            const user = store.state.Auth.currUser || store.state.Auth.userInfo
            return user?.member?.corporate_logo || '/img/member-icon/tier-logo.svg'
        })
        
        const searchQuery = ref('')
        const branches = ref([])
        const filteredMerchants = ref([])
        const selectedBranch = ref('')
        const selectedMerchant = ref('')
        const startDate = ref(null)
        const endDate = ref(null)
        const isFilterModalOpen = ref(false)
        const isMobile = ref(window.innerWidth < 992)

        window.addEventListener('resize', () => {
            isMobile.value = window.innerWidth < 992
        })
        
        const fetchNewsletters = (page = 1) => {
            const params = {
                page: page,
                limit: 10,
                filter: {},
                search: searchQuery.value
            }

            if (selectedBranch.value) params.filter.branch_id = selectedBranch.value
            if (selectedMerchant.value) params.filter.merchant_id = selectedMerchant.value
            if (startDate.value && endDate.value) {
                params.filter.date_range = `${startDate.value}, ${endDate.value}`
            }
            
            store.dispatch('ClientNewsletter/read', params)
        }

        const fetchFilters = () => {
            // Reusing TransactionHistory logic to fetch filter data
             store.dispatch('TransactionHistory/read')
                .then((res) => {
                    const filterData = res.data.filter.filter
                    branches.value = filterData
                    updateMerchants()
                })
                .catch(err => console.log('Failed to fetch filters', err))
        }
        
        const handleSearch = () => {
             fetchNewsletters(1)
        }
        
        const clearSearch = () => {
             searchQuery.value = ''
        }
        
        const scrollToTop = () => {
            if (typeof window !== 'undefined') {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
                const memberMain = document.querySelector('.member-main')
                if (memberMain) {
                    memberMain.scrollTop = 0
                }
            }
        }

        const navPage = (page) => {
            fetchNewsletters(page)
            scrollToTop()
        }
        
        const goToDetail = (id) => {
            // Validate tenant access before navigation
            const newsletter = newsletters.value.find(n => n.id === id)
            if (newsletter && !validateNewsletterAccess(newsletter)) {
                console.warn('[Newsletter] Access denied - tenant mismatch')
                trackSecurityEvent('newsletter_list_access_denied', {
                    newsletterId: id,
                    newsletterTenant: newsletter.corporate_domain
                })
                // Show error message to user
                alert('You do not have permission to view this newsletter.')
                return
            }
            
            store.dispatch('ClientNewsletter/markAsRead', id)
            router.push(`/client/newsletters/${id}`)
        }
        /**
         * Validate newsletter access based on tenant context
         * @param {Object} newsletter - Newsletter object
         * @returns {boolean} True if access is valid
         */
        const validateNewsletterAccess = (newsletter) => {
            if (!newsletter) return false
            
            try {
                const currentUser = store.state.Auth.currUser || store.state.Auth.userInfo
                const userTenant = currentUser?.member?.corporate_domain
                const newsletterTenant = newsletter.corporate_domain
                
                // If tenant info available, validate match
                if (userTenant && newsletterTenant) {
                    return userTenant === newsletterTenant
                }
                
                // Allow if tenant info not available (backend will enforce)
                return true
            } catch (err) {
                console.error('Newsletter access validation error:', err)
                return true // Allow on error (backend will enforce)
            }
        }
        
        /**
         * Track security events for monitoring
         * @param {string} eventType - Type of security event
         * @param {Object} details - Additional details
         */
        const trackSecurityEvent = (eventType, details = {}) => {
            try {
                const securityEvent = {
                    timestamp: new Date().toISOString(),
                    eventType,
                    source: 'newsletter_list',
                    userId: store.state.Auth.currUser?.id || store.state.Auth.userInfo?.id,
                    ...details
                }
                
                console.warn('[Security Event]', JSON.stringify(securityEvent))
                
                window.dispatchEvent(new CustomEvent('security-event', {
                    detail: securityEvent
                }))
            } catch (err) {
                console.error('Failed to track security event:', err)
            }
        }
        
        const formatDate = (date) => {
             if (!date) return ''
             return new Date(date).toLocaleDateString('en-US', {
                 year: 'numeric',
                 month: 'short',
                 day: '2-digit'
             })
        }

        const updateMerchants = () => {
            if (selectedBranch.value === '') {
                let allMerchants = []
                branches.value.forEach(branch => {
                    allMerchants = allMerchants.concat(branch.merchants)
                })
                filteredMerchants.value = allMerchants
            } else {
                const selected = branches.value.find(branch => branch.id === selectedBranch.value) // Use ID match
                filteredMerchants.value = selected ? selected.merchants : []
            }

            if (!selectedMerchant.value) {
                selectedMerchant.value = ''
            }
        }

        const applyFilter = () => {
            fetchNewsletters(1)
            isFilterModalOpen.value = false
        }

        const clearFilter = () => {
            selectedBranch.value = ''
            selectedMerchant.value = ''
            startDate.value = null
            endDate.value = null
            searchQuery.value = '' 
            fetchNewsletters(1)
            isFilterModalOpen.value = false
        }
        
        watch(selectedBranch, () => {
             if (selectedBranch.value === '') selectedMerchant.value = ''
        })

        let searchTimeout = null
        watch(searchQuery, () => {
            if (searchTimeout) clearTimeout(searchTimeout)
            searchTimeout = setTimeout(() => {
                fetchNewsletters(1)
            }, 500)
        })

        onMounted(() => {
            const _meta = { ...meta.value, page: 1 }
            store.commit('ClientNewsletter/setMeta', _meta)
            fetchNewsletters(1)
            fetchFilters()
        })

        onBeforeUnmount(() => {
            document.body.style.overflow = ''
        })

        watch(isFilterModalOpen, (val) => {
            document.body.style.overflow = val ? 'hidden' : ''
        })

        const truncate = (text, length = 50) => {
            if (!text) return ''
            if (text.length <= length) return text
            return text.substring(0, length) + '..'
        }

        // Check if newsletter module is enabled via feature flag
        const isNewsletterModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.newsletterModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        return {
            loading,
            newsletters,
            meta,
            searchQuery,
            handleSearch,
            navPage,
            goToDetail,
            formatDate,
            branches,
            filteredMerchants,
            selectedBranch,
            selectedMerchant,
            startDate,
            endDate,
            updateMerchants,
            applyFilter,
            clearFilter,
            clearSearch,
            isFilterModalOpen,
            isMobile,
            truncate,
            defaultLogo,
            isNewsletterModuleEnabled
        }
    }
}
</script>

<style scoped>
/* Feature Disabled State (Desktop - large centered card) */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Feature Disabled Inline State (Mobile) */
.feature-disabled-inline {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px 24px;
    background: #f9fafb;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    margin-top: 16px;
}

.feature-disabled-inline i {
    font-size: 24px;
    color: #9ca3af;
    flex-shrink: 0;
}

.feature-disabled-inline h4 {
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 4px;
}

.feature-disabled-inline p {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
}

.feature-disabled-inline.mobile-disabled {
    margin: 12px 0;
    padding: 16px;
}

/* Page Layout */
.top-background {
    position: absolute;
    top: 0;
    left: 0;
    color: var(--primary-color, #F27178)
}

.page-container {
    width: 100%;
    min-height: 100vh;
}

.desktop-view {
    margin: 0 auto;
    padding: 24px;
}

.page-header {
    margin-bottom: 32px;
}

.page-title {
    font-size: 28px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
}

.page-subtitle {
    font-size: 14px;
    color: #6B7280;
}

.content-wrapper {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 24px;
    align-items: start;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.main-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.sidebar-menu {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.filter-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05); /* Enhanced shadow */
}

.sidebar-search {
    margin-bottom: 24px; /* Space between search and filter card */
}

.card-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 20px;
}
/* Removed .search-card .card-title style as it's no longer used */

.filter-group {
    margin-bottom: 20px;
}

.filter-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
}

/* Form Elements */
.search-box {
    position: relative;
    width: 100%;
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    cursor: pointer;
}

.clear-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    cursor: pointer;
    transition: color 0.2s;
    font-size: 14px;
}

.clear-icon:hover {
    color: #6b7280;
}

.search-box input {
    width: 100%;
    padding: 12px 40px 12px 40px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: all 0.2s;
}

.search-box input:focus {
    border-color: var(--primary-border, #ef4444);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(239, 68, 68, 0.1));
}

/* Ensure search box background is white in sidebar context */
.sidebar-search .search-box input {
    background: white;
}

.custom-select {
    position: relative;
}

.custom-select select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    background: white;
    cursor: pointer;
    appearance: none;
    outline: none;
    transition: all 0.2s;
}

.custom-select select:focus {
    border-color: var(--primary-border, #ef4444);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(239, 68, 68, 0.1));
}

.select-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6B7280;
}

.date-range-inputs {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 8px;
}

.date-range-inputs.mobile {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.date-range-inputs.mobile .date-separator {
    align-self: flex-start;
    margin: 4px 0;
}

.date-separator {
    color: #6B7280;
    font-size: 14px;
}

.button-group {
    display: flex;
    margin-left: auto;
    gap: 12px;
}

.btn-apply,
.btn-clear {
    flex: 1;
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-apply {
    background-color: var(--primary-color, #F93E3E);
    color: white;
}

.btn-apply:hover {
    opacity: 0.9;
}

.btn-clear {
    background-color: white;
    color: #374151;
    border: 1px solid #E5E7EB;
}

.btn-clear:hover {
    background-color: #F9FAFB;
}

/* Newsletter List Items */
.newsletter-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.newsletter-card {
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background-color: white;
    border-radius: 12px;
    gap: 12px;
    padding: 16px;
    cursor: pointer;
    border: 1px solid #e1e4ea79;
    transition: background-color 0.2s;
     box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05); /* Added subtle shadow for depth */
}

.newsletter-card:hover {
    background-color: #f9fafb;
}

.newsletter-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.newsletter-image {
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    background-color: white;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.newsletter-image img {
    margin: auto;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.newsletter-detail {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.newsletter-title {
    font-size: 14px;
    font-weight: bold;
    color: #111827;
    line-height: 1.4;
    margin: 0;
    overflow-wrap: break-word;
    word-break: break-word;
}

.newsletter-publisher {
    font-size: 12px;
    font-weight: normal;
    color: #111827;
    line-height: 100%;
    margin: 0;
}

.newsletter-date {
    font-size: 12px;
    font-weight: normal;
    color: #99A0AE;
    line-height: 100%;
    white-space: nowrap;
}

.newsletter-badge {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 38px 38px 0 0;
    border-color: var(--primary-border) transparent transparent transparent;
    z-index: 2;
}

.newsletter-badge span {
    position: absolute;
    top: -32px;
    left: 3px;
    font-size: 8px;
    font-weight: bold;
    color: white;
    text-transform: uppercase;
    white-space: nowrap;
    line-height: normal;
    transform: rotate(-45deg);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

.newsletter-chevron {
    width: 20px;
    height: 20px;
    align-self: center;
    flex-shrink: 0;
    opacity: 0.6;
}

.empty-state {
    text-align: center;
    color: #9ca3af;
    padding: 40px;
    font-size: 14px;
    border-radius: 12px;
}

@media (max-width: 1280px) {
    .desktop-view {
        padding: 16px;
    }
    .content-wrapper {
        grid-template-columns: 1fr 280px;
        gap: 16px;
    }
    .filter-card {
        padding: 20px 16px;
    }

    .date-range-inputs {
        gap: 6px;
    }

    .date-picker :deep(.date-input-trigger) {
        padding: 8px 10px;
        gap: 4px;
    }
}

/* Mobile View Styles */
@media (max-width: 991px) {
    .mobile-view {
        padding: 20px;
        padding-bottom: 96px;
    }

    .mobile-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 64px;
        margin-bottom: 24px;
    }

    .mobile-title {
        font-size: 18px;
        font-weight: 520;
        margin: 0 !important;
        color: #111827;
    }

    .filter-button {
        background: var(--primary-color, #F93E3E);
        border: none;
        padding: 8px 10px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    /* Modal Styles */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1000;
        display: flex;
        align-items: flex-end; /* Bottom sheet on mobile */
        justify-content: center;
        animation: fadeIn 0.2s ease-out;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .modal-content {
        background: white;
        width: 100%;
        max-width: 600px;
        border-radius: 20px 20px 0 0;
        max-height: 80vh;
        overflow-y: auto;
        animation: slideUp 0.3s ease-out;
    }

    @keyframes slideUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #E5E7EB;
    }

    .modal-header h3 {
        font-size: 18px;
        font-weight: 600;
        color: #111827;
        margin: 0;
    }

    .modal-body {
        padding: 20px;
    }

    .close-button {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        color: #6B7280;
    }
}
</style>
